/* eslint-disable @typescript-eslint/camelcase */
import React, { useRef } from 'react';
import i18n from 'i18n';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import Grid from '@material-ui/core/Grid';
import { CardMonitorProps } from 'components/CardMonitor';
import { IconButton, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { Input as InputIcon } from '@material-ui/icons';
import Loading from 'components/Loading';
import {
  BlockedEnvironmentsFields,
  EnvironmentHealthFields,
  InstanceStatusFields,
  PeriodCountAccess,
  InstancesWithSessionTimeoutFields,
} from 'modules/monitor/interfaces';
import {
  getBlockedEnvironmentsRequest,
  getEnvironmentHealthRequest,
  getInstanceStatusRequest,
  getEnvironmentAndCeleryQueueStatusRequest,
  getSessionsHistoryMonitorGraphRequest,
  getInstancesWithSessionTimeoutRequest,
  getEnvironmentsWithFailedZeroTrustRequest,
} from 'modules/monitor/actions';
import TodayIcon from '@material-ui/icons/Today';
import CloseIcon from '@material-ui/icons/Close';
import CachedIcon from '@material-ui/icons/Cached';
import clsx from 'clsx';
import PageHeader from 'components/PageHeader';
import { useArrowListener } from 'hooks';
import SOPCard from 'containers/Panel/Incidents/SOPCard';
import {
  CompanyData,
  PartnerData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { Formik } from 'formik';
import DatePicker from 'components/DatePicker';
import Button from 'components/Button';
import { formatDateForDateFilter, getCompanyName } from 'utils/tsHelpers';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { cyrb53Hash } from 'utils/helpers';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import { getAllEnvironmentsOperatorRequest } from 'modules/environments/actions';
import {
  AllEnvironmentsFields,
  EnvironmentStatus,
  ListAllEnvironmentsDataProps,
} from 'modules/environments/interfaces';
import { SearchBar } from 'components/SearchBar';
import styles from './styles';
import InstanceStatusTable from './InstanceStatusTable';
import EnvironmentHealthTable from './EnvironmentHealthTable';
import BlockedEnvironmentsTable from './BlockedEnvironmentsTable';
import FollowUpCard from './FollowUpCard';
import SessionsHistoryCard from './SessionsHistoryCard';
import InstancesHistoryCard from './InstancesHistoryCard';
import ClientsHistory from './ClientsHistory';

interface DatesProps {
  start_date: string;
  end_date: string;
}

export enum MonitorCardsEnum {
  environmentMissing = 'environmentMissing',
  environmentBlock = 'environmentBlock',
  instanceProblem = 'instanceProblem',
}

const initialQuery = {
  offset: 0,
  limit: 50,
  status: '',
  period: '',
  search: '',
};

interface Props {
  blockedEnvironmentsData: BlockedEnvironmentsFields;
  environmentHealthData: EnvironmentHealthFields;
  instanceStatusData: InstanceStatusFields;
  instancesWithSessionTimeout: InstancesWithSessionTimeoutFields;
  isLoading: boolean;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  periodFilterSessions: PeriodCountAccess;
  environmentsList: ListAllEnvironmentsDataProps;
  isLoadingEnvironment: boolean;
  getBlockedEnvironmentsRequest: (props) => void;
  getEnvironmentHealthRequest: (props) => void;
  getInstanceStatusRequest: (props) => void;
  getEnvironmentAndCeleryQueueStatusRequest: () => void;
  getEnvironmentsWithFailedZeroTrustRequest: (props) => void;
  getInstancesWithSessionTimeoutRequest: (props) => void;
  getSessionsHistoryMonitorGraphRequest: ({
    intervalTime,
    start_date,
    end_date,
  }) => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
  getAllEnvironmentsOperatorRequest: (props) => void;
}

const IncidentsPanel = ({
  blockedEnvironmentsData,
  environmentHealthData,
  instanceStatusData,
  instancesWithSessionTimeout,
  isLoading,
  role,
  companySelected,
  periodFilterSessions,
  environmentsList,
  isLoadingEnvironment,
  getBlockedEnvironmentsRequest,
  getEnvironmentHealthRequest,
  getInstanceStatusRequest,
  getEnvironmentAndCeleryQueueStatusRequest,
  getEnvironmentsWithFailedZeroTrustRequest,
  getInstancesWithSessionTimeoutRequest,
  getSessionsHistoryMonitorGraphRequest,
  selectCompany,
  selectPartner,
  getAllEnvironmentsOperatorRequest,
}: Props) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles({});
  const [dateFilter, setDateFilter] = React.useState(true);
  const { section } = useParams<{ section: string }>();
  const [dates, setDates] = React.useState<DatesProps>({
    start_date: '',
    end_date: '',
  });
  const history = useHistory();
  const {
    query,
    itemOrder,
    searchValue,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
    handleOrder,
  } = useTableFilters(initialQuery);

  const [queryCards] = React.useState(initialQuery);

  const { arrowLeft, arrowRight } = useArrowListener();

  const sectionRef = useRef<HTMLDivElement>(null);

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  React.useEffect(() => {
    getEnvironmentHealthRequest(queryCards);
    getBlockedEnvironmentsRequest(queryCards);
    getInstanceStatusRequest(queryCards);
    getInstancesWithSessionTimeoutRequest(queryCards);
    getEnvironmentAndCeleryQueueStatusRequest();
    getEnvironmentsWithFailedZeroTrustRequest(queryCards);
  }, [
    getBlockedEnvironmentsRequest,
    getEnvironmentHealthRequest,
    getInstanceStatusRequest,
    getInstancesWithSessionTimeoutRequest,
    getEnvironmentAndCeleryQueueStatusRequest,
    getEnvironmentsWithFailedZeroTrustRequest,
    queryCards,
  ]);

  const blockedEnvironmentsCount = blockedEnvironmentsData?.count;
  const environmentHealthCount = environmentHealthData?.count;
  const instanceStatusCount = instanceStatusData?.count;
  const instancesWithSessionTimeoutCount = instancesWithSessionTimeout?.count;

  const refreshPage = () => {
    window.location.reload();
  };

  const [
    selectedCard,
    setSelectedCard,
  ] = React.useState<MonitorCardsEnum | null>(null);

  const handleCardClick = (type: MonitorCardsEnum) => () => {
    if (String(selectedCard) === String(type)) setSelectedCard(null);
    else setSelectedCard(type);
  };

  const getEnvironments = () => {
    getAllEnvironmentsOperatorRequest({
      ...query,
      completion_date_expires_soon: true,
    });
  };

  React.useEffect(() => {
    getEnvironments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  if (section === 'envStatus') {
    scrollToSection();
  }

  const headers = [
    {
      key: '',
      i18n: i18n.t('ACCESS_KEY'),
      centerTitle: true,
    },
    {
      key: 'environment',
      i18n: i18n.t('MONITOR.ENVIRONMENT_NAME'),
      order: true,
    },
    {
      key: 'env_status',
      i18n: i18n.t('STATUS_KEY'),
      order: true,
    },
    {
      key: 'completion_date_expire_at_days',
      i18n: i18n.t('DAYS_TO_EXPIRE_KEY'),
      order: false,
      centerTitle: true,
    },
  ];

  const getTableByCard = React.useCallback(
    (cardType: MonitorCardsEnum): JSX.Element =>
      ({
        [MonitorCardsEnum.environmentBlock]: (
          <BlockedEnvironmentsTable
            blockedEnvironmentsData={blockedEnvironmentsData}
          />
        ),
        [MonitorCardsEnum.environmentMissing]: (
          <EnvironmentHealthTable
            environmentHealthData={environmentHealthData}
          />
        ),
        [MonitorCardsEnum.instanceProblem]: (
          <InstanceStatusTable instanceStatusData={instanceStatusData} />
        ),
      }[cardType]),
    [blockedEnvironmentsData, environmentHealthData, instanceStatusData]
  );

  const monitorTable: JSX.Element = React.useMemo(() => {
    if (!selectedCard) return <></>;
    const table = getTableByCard(selectedCard);
    return (
      <Grid
        key="monitor-table"
        className={`${classes.table} ${classes.fadein}`}
      >
        {table}
      </Grid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard, getTableByCard]);

  const monitorCards: Array<Partial<CardMonitorProps> & {
    type: MonitorCardsEnum;
  }> = [
    {
      type: MonitorCardsEnum.environmentMissing,
      title: i18n.t('MONITOR.CARD_ENVIRONMENT_HEALTH'),
      count: environmentHealthCount,
      onClick: handleCardClick(MonitorCardsEnum.environmentMissing),
    },
    {
      type: MonitorCardsEnum.instanceProblem,
      title: i18n.t('MONITOR.CARD_INSTANCES'),
      count: instanceStatusCount + instancesWithSessionTimeoutCount,
      onClick: handleCardClick(MonitorCardsEnum.instanceProblem),
    },
    {
      type: MonitorCardsEnum.environmentBlock,
      title: i18n.t('MONITOR.CARD_BLOCKED_ENVIRONMENTS'),
      count: blockedEnvironmentsCount,
      onClick: handleCardClick(MonitorCardsEnum.environmentBlock),
    },
  ];

  const isCardSelected = (type: MonitorCardsEnum): boolean | undefined => {
    if (selectedCard === null) return undefined;
    return selectedCard === type;
  };

  React.useEffect(() => {
    const indexOfSelectedCard = monitorCards?.findIndex(
      card => isCardSelected(card?.type) || false
    );

    let newSelectedCard: MonitorCardsEnum | undefined;

    if (arrowLeft && indexOfSelectedCard > 0) {
      newSelectedCard = monitorCards[indexOfSelectedCard - 1]?.type;
    } else if (arrowRight && indexOfSelectedCard + 1 < monitorCards?.length) {
      newSelectedCard = monitorCards[indexOfSelectedCard + 1]?.type;
    }

    if (newSelectedCard) setSelectedCard(newSelectedCard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrowLeft, arrowRight]);

  React.useEffect(() => {
    getSessionsHistoryMonitorGraphRequest({
      intervalTime: periodFilterSessions,
      start_date: dates?.start_date,
      end_date: dates?.end_date,
    });
  }, [getSessionsHistoryMonitorGraphRequest, periodFilterSessions, dates]);

  const handleSubmit = (values: DatesProps) => {
    setDates({
      start_date: formatDateForDateFilter(values?.start_date),
      end_date: formatDateForDateFilter(values?.end_date),
    });
  };

  const redirectToContext = (item: AllEnvironmentsFields) => {
    selectCompany({
      id: item?.company_uuid,
      name: getCompanyName(item),
      shortName: getCompanyName(item),
    });
    selectPartner({
      id: item?.partner_uuid,
      name: item?.partner_name,
      shortName: item?.partner_name,
    });
    history.push(
      `/environments/${item?.environment_uuid}/configuration/status`
    );
  };

  const mapEnvStatus = (status: EnvironmentStatus | string) => {
    const envStatusMap = {
      [EnvironmentStatus.PENDING]: i18n.t('ENVIRONMENT_STATUS.PENDING_KEY'),
      [EnvironmentStatus.DEMONSTRATION]: i18n.t(
        'ENVIRONMENT_STATUS.DEMONSTRATION_KEY'
      ),
      [EnvironmentStatus.DEPLOYMENT]: i18n.t(
        'ENVIRONMENT_STATUS.DEPLOYMENT_KEY'
      ),
      [EnvironmentStatus.DEVELOPMENT]: i18n.t(
        'ENVIRONMENT_STATUS.DEVELOPMENT_KEY'
      ),
      [EnvironmentStatus.HOMOLOGATION]: i18n.t(
        'ENVIRONMENT_STATUS.HOMOLOGATION_KEY'
      ),
      [EnvironmentStatus.MIGRATION]: i18n.t('ENVIRONMENT_STATUS.MIGRATION_KEY'),
      [EnvironmentStatus.PRODUCTION]: i18n.t(
        'ENVIRONMENT_STATUS.PRODUCTION_KEY'
      ),
    };

    return envStatusMap[status];
  };

  return (
    <>
      <Grid>
        <PageHeader
          title={i18n.t('MONITOR.INCIDENTS_PANEL_TITLE')}
          subtitle={i18n.t('MONITOR.INCIDENTS_PANEL_SUBTITLE')}
          rightSideComponent={
            <Grid className={classes.buttonGrid}>
              <Button
                startIcon={<CachedIcon />}
                onClick={refreshPage}
                className={clsx(classes.refreshButton, {
                  [classes.refreshButtonGreen]: inAdminView(
                    role,
                    companySelected
                  ),
                  [classes.refreshButtonGray]: inSuperAdminView(
                    role,
                    companySelected
                  ),
                  [classes.refreshButtonBlue]: inOperatorView(
                    role,
                    companySelected
                  ),
                  [classes.refreshButtonYellow]: companySelected,
                })}
              >
                {i18n.t('MONITOR.RELOAD')}
              </Button>
            </Grid>
          }
        />
      </Grid>
      {isLoading ? (
        <Loading />
      ) : (
        <Grid
          container
          justify="center"
          alignContent="center"
          className={classes.pageBody}
        >
          <Grid container spacing={2}>
            <Grid item className={classes.sopCardContainer}>
              <Grid item xs={4}>
                <Typography className={classes.titleCard}>{'SOP:'}</Typography>
              </Grid>
              <Grid container className={classes.card}>
                <SOPCard />
              </Grid>
            </Grid>
            <Grid item className={classes.sopCardContainer}>
              <Grid item xs={4}>
                <Typography className={classes.titleCard}>
                  {i18n.t('MONITOR.INCIDENTS_PANEL_FOLLOW_UP')}
                  {':'}
                </Typography>
              </Grid>
              <Grid container className={classes.card}>
                <FollowUpCard />
              </Grid>
            </Grid>
            <Grid item className={classes.sopCardContainer}>
              <Grid item xs={4}>
                <Typography className={classes.titleCard}>
                  {i18n.t('HISTORY_SOP_KEY')}
                  {':'}
                </Typography>
              </Grid>
              <Grid container className={classes.card}>
                <ClientsHistory />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            className={clsx(classes.noDateFilters, {
              [classes.dateFiltersSuperAdmin]:
                !dateFilter && inSuperAdminView(role, companySelected),
              [classes.dateFiltersAdmin]:
                !dateFilter && inAdminView(role, companySelected),
              [classes.dateFiltersOperator]:
                !dateFilter && inOperatorView(role, companySelected),
            })}
          >
            <Grid container justify="center">
              <Grid item className={classes.filterSelector}>
                {dateFilter ? (
                  <Button
                    startIcon={<TodayIcon />}
                    variant="outlined"
                    onClick={() => setDateFilter(false)}
                    className={classes.applyButton}
                  >
                    {i18n.t('APPLY_DATE_FILTERS_KEY')}
                  </Button>
                ) : (
                  <>
                    <Formik initialValues={dates} onSubmit={handleSubmit}>
                      {({ setFieldValue, submitForm, values }) => (
                        <Grid spacing={2} container justify="flex-start">
                          <IconButton
                            onClick={() => {
                              setDateFilter(true);
                              setDates({ start_date: '', end_date: '' });
                              setFieldValue('start_date', null);
                              setFieldValue('end_date', null);
                            }}
                          >
                            <CloseIcon className={classes.closeIcon} />
                          </IconButton>
                          <DatePicker
                            id="start_date"
                            type="datetimeFilter"
                            label={i18n.t('START_TIME_KEY')}
                            keyValue="start_date"
                            value={values?.start_date}
                            setFieldsValue={setFieldValue}
                            shrink={
                              values?.start_date !== '' &&
                              values?.start_date !== null &&
                              values?.start_date !== undefined
                            }
                          />
                          <DatePicker
                            id="end_date"
                            type="datetimeFilter"
                            label={i18n.t('END_TIME_KEY')}
                            keyValue="end_date"
                            value={values?.end_date}
                            minDate={values?.start_date}
                            setFieldsValue={setFieldValue}
                            shrink={
                              values?.end_date !== '' &&
                              values?.end_date !== null &&
                              values?.end_date !== undefined
                            }
                          />
                          <Grid item>
                            <Button
                              id="submit-button"
                              onClick={submitForm}
                              submitButton
                              disabled={
                                !values?.start_date ||
                                !values?.end_date ||
                                values?.start_date > values?.end_date
                              }
                            >
                              {i18n.t('APPLY_KEY')}
                            </Button>
                            {(values?.start_date || values?.end_date) && (
                              <Button
                                id="clear-button"
                                onClick={() => {
                                  setFieldValue('start_date', null);
                                  setFieldValue('end_date', null);
                                }}
                                deleteButton
                              >
                                {i18n.t('CLEAR_FORM_KEY')}
                              </Button>
                            )}
                          </Grid>
                        </Grid>
                      )}
                    </Formik>
                  </>
                )}
              </Grid>
            </Grid>
            <SessionsHistoryCard dateFilter={dateFilter} />
            <InstancesHistoryCard dateFilter={dateFilter} />
          </Grid>
          <Grid
            innerRef={sectionRef}
            container
            className={clsx(classes.envStatusGrid, {
              [classes.envStatusGridAdmin]:
                inAdminView(role, companySelected) && section === 'envStatus',
              [classes.envStatusGridSuperAdmin]:
                inSuperAdminView(role, companySelected) &&
                section === 'envStatus',
              [classes.envStatusGridOperator]:
                inOperatorView(role, companySelected) &&
                section === 'envStatus',
            })}
          >
            <Grid item xs={4}>
              <Typography className={classes.titleCard}>
                {i18n.t('ENVIRONMENTS_STATUS_TO_EXPIRE_KEY')}
                {':'}
              </Typography>
            </Grid>
            <Grid container>
              <SearchBar
                id="search-instances"
                isLoading={isLoadingEnvironment}
                searchValue={searchValue}
                handleSearch={handleSearch}
                setSearchValue={setSearchValue}
              />
            </Grid>
            <Grid item container>
              <TableItems
                header={
                  <>
                    {headers.map(item => (
                      <TableCell
                        key={item?.key}
                        centerTitle={item?.centerTitle}
                        markOderItem={itemOrder === item?.key}
                        order={item?.order}
                        handleOrder={() =>
                          item?.order ? handleOrder(item?.key) : null
                        }
                      >
                        {item?.i18n}
                      </TableCell>
                    ))}
                  </>
                }
                renderItems={(item: AllEnvironmentsFields, index) => (
                  <TableRow key={cyrb53Hash(index)}>
                    <TableCell centerTitle textAlignCenter>
                      <IconButton
                        className={classes.accessButton}
                        onClick={() => {
                          redirectToContext(item);
                        }}
                      >
                        <InputIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell className={classes.text}>
                      <Tooltip title={item?.environment_name}>
                        <Typography>{item?.environment_name}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.text}>
                      <Typography>{mapEnvStatus(item?.env_status)}</Typography>
                    </TableCell>
                    <TableCell
                      centerTitle
                      textAlignCenter
                      className={classes.number}
                    >
                      <Typography>
                        {item?.completion_date_expire_at_days}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                data={environmentsList}
                isLoading={isLoadingEnvironment}
                onChangePage={handlePageChange}
                onChangeLimit={handleLimitChange}
                notFoundText={
                  <Typography className={classes.notFoundText}>
                    {i18n.t('MONITOR.NO_RESULTS_INSTANCES')}
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {monitorTable}
    </>
  );
};

const mapStateToProps = ({
  monitor: {
    blockedEnvironmentsData,
    environmentHealthData,
    instanceStatusData,
    sessionsHistoryMonitorGraphFields,
    isLoading,
    periodFilterSessions,
    instancesWithSessionTimeout,
  },
  authenticate: { companySelected, data },
  environments: { environmentsList, isLoading: isLoadingEnvironment },
}: ApplicationState) => ({
  blockedEnvironmentsData,
  environmentHealthData,
  instanceStatusData,
  instancesWithSessionTimeout,
  companySelected,
  sessionsHistoryMonitorGraphFields,
  role: data?.role,
  isLoading,
  periodFilterSessions,
  environmentsList,
  isLoadingEnvironment,
});

export default connect(mapStateToProps, {
  getBlockedEnvironmentsRequest,
  getEnvironmentHealthRequest,
  getInstanceStatusRequest,
  getEnvironmentAndCeleryQueueStatusRequest,
  getEnvironmentsWithFailedZeroTrustRequest,
  getInstancesWithSessionTimeoutRequest,
  getSessionsHistoryMonitorGraphRequest,
  selectCompany,
  selectPartner,
  getAllEnvironmentsOperatorRequest,
})(IncidentsPanel);
